import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/joy';
import type { OpenedOrderAcceptance } from './jobCard.service';
import { renderPdfSourceToPageImages } from './pdfPageImages';

type JobCardOaPreviewProps = {
  preview: OpenedOrderAcceptance;
};

export default function JobCardOaPreview({ preview }: JobCardOaPreviewProps) {
  const [pages, setPages] = useState<string[]>([]);
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'failed'>('idle');

  useEffect(() => {
    if (preview.kind !== 'pdf') {
      setPages([]);
      setState('idle');
      return;
    }
    let cancelled = false;
    setState('loading');
    setPages([]);
    void renderPdfSourceToPageImages(preview.url)
      .then((images) => {
        if (cancelled) return;
        setPages(images);
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [preview.url, preview.kind]);

  if (preview.kind === 'image') {
    return (
      <Box
        sx={{
          height: '100%',
          overflow: 'auto',
          WebkitOverflowScrolling: 'touch',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          p: 1,
          bgcolor: 'neutral.800',
        }}
      >
        <Box
          component="img"
          src={preview.url}
          alt={preview.name}
          sx={{ maxWidth: '100%', height: 'auto', display: 'block' }}
        />
      </Box>
    );
  }

  if (preview.kind === 'text') {
    return (
      <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
        <Typography component="pre" level="body-sm" sx={{ whiteSpace: 'pre-wrap', m: 0, fontFamily: 'ui-monospace, monospace' }}>
          {preview.text || ''}
        </Typography>
      </Box>
    );
  }

  if (preview.kind !== 'pdf') {
    return (
      <Stack gap={1.5} sx={{ p: 2 }}>
        <Typography level="body-sm">
          This file type may not preview in the browser. Try opening it in a new tab.
        </Typography>
        <Button
          component="a"
          href={preview.url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ minHeight: 44 }}
        >
          Open in new tab
        </Button>
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        bgcolor: 'neutral.800',
        p: { xs: 0.75, sm: 1.25 },
      }}
    >
      {state === 'loading' || state === 'idle' ? (
        <Stack sx={{ minHeight: 240 }} alignItems="center" justifyContent="center" gap={1}>
          <CircularProgress size="sm" />
          <Typography level="body-sm" sx={{ color: 'neutral.200' }}>Loading pages…</Typography>
        </Stack>
      ) : state === 'failed' ? (
        <Stack gap={1.25} sx={{ p: 2, bgcolor: 'background.body', borderRadius: 'sm' }}>
          <Typography level="body-sm">
            This phone cannot show the file inline. Open it in a new tab instead.
          </Typography>
          <Button
            component="a"
            href={preview.url}
            target="_blank"
            rel="noopener noreferrer"
            download={preview.name}
            sx={{ minHeight: 44 }}
          >
            Open file
          </Button>
        </Stack>
      ) : (
        <Stack gap={1.25}>
          {pages.map((src, index) => (
            <Box
              key={`${preview.name}-${index + 1}`}
              component="img"
              src={src}
              alt={`${preview.name} page ${index + 1}`}
              sx={{
                display: 'block',
                width: '100%',
                height: 'auto',
                bgcolor: '#fff',
                borderRadius: 'sm',
              }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
